import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
  type ReactNode,
} from 'react'
import { useMutation, useQuery, type UseMutationResult, type UseQueryResult } from '@tanstack/react-query'
import { api } from '../lib/api'
import { useI18n, useT } from '../lib/i18n'
import { useDictation } from '../lib/dictation'
import { Icon } from './ui'
import type { AskDraft } from './askDraft'

export interface Answer {
  type?: 'answer'
  bullets: string[]
  note?: string
  link?: { label: string; to: string }
  understood: string
}

/** What /ask returns when the sentence was an instruction, not a question. */
export type Draft = AskDraft & { type: 'draft' }

/**
 * One thing a carrier's sentence has PREPARED, and the endpoint that will do
 * it when -- and only when -- he presses.
 *
 * `call` is a real endpoint, the same one the button on its own screen uses.
 * It is not a privilege the chat box grants: the request goes out with his own
 * token and the server re-checks that the row is his, exactly as it does for
 * every other caller. Nothing here can do anything he could not already do.
 */
export interface ActionStep {
  title: string
  lines: { label: string; value: string }[]
  call: { method: 'POST'; path: string; body: Record<string, unknown> }
  confirm: string
  done: string
}

/** What /ask returns for a carrier's instruction. Prepared, never performed. */
export interface Action {
  type: 'action'
  actionId: string
  understood: string
  steps: ActionStep[]
  /** Set when there is more than one step: the question, never a guess. */
  choose?: string
  /** Why nothing could be prepared. */
  blocked?: string[]
  note?: string
}

export type Reply = Answer | Draft | Action

export interface Turn { q: string; a: Reply }

interface Suggestion { key: string; text: string }

interface AskState {
  /** The compose box is open in the tab bar and the panel is up. */
  open: boolean
  setOpen: (open: boolean) => void
  /** The mockup's journey sheet, which Qafila's Ask button opens instead. */
  journey: boolean
  setJourney: (open: boolean) => void
  q: string
  setQ: (q: string) => void
  send: (question: string) => void
  history: Turn[]
  ask: UseMutationResult<Reply, Error, string>
  suggestions: UseQueryResult<{ suggestions: Suggestion[] }>
  voice: ReturnType<typeof useDictation>
  locale: string
  /** The compose input, so the bar can focus it once its morph has finished. */
  box: React.RefObject<HTMLInputElement>
}

const Ctx = createContext<AskState | null>(null)

/**
 * The conversation, held above both halves of the UI that show it.
 *
 * The compose box lives IN the tab bar (it is what the Ask button turns
 * into) and the answers live in a panel above it. Two components, one
 * conversation -- so the state sits in a provider rather than being lifted
 * through App by hand.
 */
export function AskProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false)
  const [journey, setJourney] = useState(false)
  const [q, setQ] = useState('')
  const [history, setHistory] = useState<Turn[]>([])
  const voice = useDictation()
  const { locale } = useI18n()
  const box = useRef<HTMLInputElement>(null)

  /**
   * Each opener is a key to show and the English to send: the chip's text is
   * the question the parser will be given, and the parser reads English.
   */
  const suggestions = useQuery<{ suggestions: Suggestion[] }>({
    queryKey: ['ask-suggestions'],
    queryFn: () => api('/ask/suggestions'),
    enabled: open,
    staleTime: 10 * 60_000,
  })

  const ask = useMutation<Reply, Error, string>({
    mutationFn: (question: string) =>
      // The locale the app is rendering in, so the answer comes back in the
      // language on screen rather than the one on the profile.
      api<Reply>('/ask', { method: 'POST', body: { question, locale } }),
    onSuccess: (a, question) => setHistory((h) => [...h, { q: question, a }]),
  })

  const send = useCallback((question: string) => {
    const text = question.trim()
    if (!text || ask.isPending) return
    voice.stop()
    setQ('')
    ask.mutate(text)
  }, [ask, voice])

  // Closing stops the microphone. Never leave it running because the bar
  // folded back up.
  useEffect(() => { if (!open) voice.stop() }, [open]) // eslint-disable-line react-hooks/exhaustive-deps

  const value = useMemo<AskState>(() => ({
    open, setOpen, journey, setJourney,
    q, setQ, send, history, ask, suggestions, voice, locale, box,
  }), [open, q, send, history, ask, suggestions, voice, locale])

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useAsk(): AskState {
  const v = useContext(Ctx)
  if (!v) throw new Error('useAsk must be used inside AskProvider')
  return v
}

/**
 * The bot mark. Antenna, head, two eyes, and the little side ears that stop
 * it reading as a television. Not the alert glyph it once wore -- that
 * exclamation is what the attention feed puts on an unpaid invoice.
 */
export function AskGlyph({ className = 'h-[24px] w-[24px]' }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
         strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className={className}>
      <path d="M12 2.4v2.6" />
      <circle cx="12" cy="1.9" r="1.1" fill="currentColor" stroke="none" />
      <rect x="3.8" y="6.6" width="16.4" height="12" rx="4.2" />
      <circle cx="9.2" cy="12.6" r="1.35" fill="currentColor" stroke="none" />
      <circle cx="14.8" cy="12.6" r="1.35" fill="currentColor" stroke="none" />
      <path d="M1.8 11.4v2.6M22.2 11.4v2.6" />
    </svg>
  )
}

/**
 * The compose row that the Ask button expands into.
 *
 * Rendered by the tab bar inside the expanded glass capsule. Enter sends;
 * the trailing control is the microphone while the box is empty and a send
 * arrow once there is something to send -- one slot, never two buttons.
 */
export function AskCompose() {
  const msg = useT()
  const { q, setQ, send, ask, voice, locale, box, open } = useAsk()

  return (
    <form
      className="flex min-w-0 flex-1 items-center"
      onSubmit={(e) => { e.preventDefault(); send(q) }}
    >
      <input
        ref={box}
        className="tabbar-ask-input"
        value={q}
        enterKeyHint="send"
        autoComplete="off"
        tabIndex={open ? 0 : -1}
        placeholder={voice.listening ? msg('ask.listening') : msg('askBubble.askAboutYourAccount')}
        onChange={(e) => setQ(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Escape') (e.target as HTMLInputElement).blur() }}
      />
      {q.trim()
        ? (
          <button type="submit" disabled={ask.isPending} tabIndex={open ? 0 : -1}
                  aria-label={msg('askBubble.ask')}
                  className="tabbar-ask-side text-accent disabled:opacity-40">
            <Icon name="chevron" className="h-5 w-5" />
          </button>
        )
        : voice.supported && (
          /* Dictation, not an audio message: the words go in the box and the
             person presses send. A misheard city is worse than an extra tap. */
          <button
            type="button"
            tabIndex={open ? 0 : -1}
            aria-label={msg('ask.speak')}
            aria-pressed={voice.listening}
            onClick={() => voice.listening ? voice.stop() : voice.start(locale, setQ)}
            className={`tabbar-ask-side ${voice.listening ? 'animate-pulse text-band-red' : 'text-ink-2'}`}
          >
            <Icon name="mic" className="h-5 w-5" />
          </button>
        )}
    </form>
  )
}
